// Загрузка протокола до первого кадра страницы (045: OPN-01 – OPN-04; research Р1, Р2). Запрос
// состояния протокола (minutes.state) — один на страницу, загрузчик маршрута и прогрев:
// - строка «Документов» по первому нажатию (первое нажатие двойного щелчка, Enter, касание)
//   прогревает запись — warmProtocol; запись живёт 10 с (PROTOCOL_WARM_MS) и без страницы
//   отбрасывается;
// - загрузчик маршрута при предзагрузке (наведение) данных не читает, при переходе берёт запись
//   моложе 10 с или читает заново; ошибку не бросает — она остаётся в записи, и ветви ошибок
//   страницы (ACC-005, MIN-001, сбой) показывают её без второго чтения (retryOnMount: false);
// - страница при уходе снимает запись (forgetProtocol): следующий вход читает протокол заново,
//   как до 045 (gcTime 0).
// Маршрутизатор держит прежнюю страницу, пока загрузчик ждёт, и после 300 мс показывает скелет
// протокола (pendingComponent маршрута, ProtocolSkeleton.tsx).
import type { QueryClient } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useMemo } from 'react';
import { apiKey, apiQuery } from '../../api/client.ts';

/** Сколько живёт прогретая и ещё не открытая запись состояния протокола, мс (OPN-02). */
export const PROTOCOL_WARM_MS = 10_000;

export function protocolStateKey(id: string): readonly unknown[] {
  return apiKey('minutes.state', { params: { id } });
}

/** Запрос состояния протокола — общий у страницы, загрузчика маршрута и прогрева. */
export function protocolStateQuery(id: string) {
  return {
    ...apiQuery('minutes.state', { params: { id } }),
    // Перечитывание заменило бы правки до ответа сервера: таблица обновляется по команде
    // человека («Обновить таблицу»); опрос изменений — срез 015.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    // Каждый вход на страницу — свежее состояние, а не снимок прошлого входа из кэша. Запись
    // загрузчика и прогрева живёт дольше (PROTOCOL_WARM_MS) — её снимает страница при уходе.
    gcTime: 0,
    // 045: ошибку уже прочитал загрузчик маршрута — страница её показывает, а не читает заново.
    retryOnMount: false,
    // Возврат связи не перечитывает таблицу сам: это пересоздало бы сессию посреди повтора.
    meta: { manualRefetch: true },
  };
}

/** Страница этого протокола на экране: у записи есть наблюдатель. */
function onScreen(queryClient: QueryClient, id: string): boolean {
  const query = queryClient.getQueryCache().find({ queryKey: protocolStateKey(id), exact: true });
  return (query?.getObserversCount() ?? 0) > 0;
}

/** Запись прогрета: чтение идёт или ответ моложе 10 с. */
export function isProtocolWarm(queryClient: QueryClient, id: string, now = Date.now()): boolean {
  const state = queryClient.getQueryState(protocolStateKey(id));
  if (!state) return false;
  if (state.fetchStatus === 'fetching') return true;
  return state.status === 'success' && now - state.dataUpdatedAt < PROTOCOL_WARM_MS;
}

/** Прочитать запись или взять прогретую; живёт 10 с, пока страница её не возьмёт. */
function fetchState(queryClient: QueryClient, id: string) {
  return queryClient.fetchQuery({
    ...protocolStateQuery(id),
    gcTime: PROTOCOL_WARM_MS,
    staleTime: PROTOCOL_WARM_MS,
  });
}

/** Прогрев по первому нажатию на строку протокола (OPN-02): ответ ждёт перехода 10 с. */
export function warmProtocol(queryClient: QueryClient, id: string): void {
  if (onScreen(queryClient, id) || isProtocolWarm(queryClient, id)) return;
  // Сбой прогрева не показывается: загрузчик перехода прочитает протокол сам.
  fetchState(queryClient, id).catch(() => undefined);
}

/**
 * Данные протокола до перехода (OPN-03): прогретая запись или чтение. Страница этого протокола
 * уже на экране (смена ?item= и прочих параметров адреса) — её запись не трогается. Ошибка
 * остаётся в записи: её показывает страница.
 */
export async function loadProtocol(queryClient: QueryClient, id: string): Promise<void> {
  if (onScreen(queryClient, id)) return;
  try {
    await fetchState(queryClient, id);
  } catch {
    // ACC-005, MIN-001, сбой — ветви ошибок страницы, без второго чтения (retryOnMount: false).
  }
}

/** Загрузчик маршрута протокола: при предзагрузке (наведение) — только код страницы (OPN-01). */
export function protocolLoader({
  context,
  params,
  preload,
}: {
  context: { queryClient: QueryClient };
  params: { id: string };
  preload: boolean;
}): Promise<void> | undefined {
  return preload ? undefined : loadProtocol(context.queryClient, params.id);
}

/**
 * Страница ушла — запись снимается, следующий вход читает протокол заново. StrictMode снимает
 * и ставит страницу тем же кадром: запись снимается, только если страницы этого протокола нет.
 */
export function forgetProtocol(queryClient: QueryClient, id: string): void {
  setTimeout(() => {
    if (onScreen(queryClient, id)) return;
    queryClient.removeQueries({ queryKey: protocolStateKey(id), exact: true });
  }, 0);
}

/**
 * Строка протокола «Документов» (OPN-01, OPN-02): наведение и фокус грузят код страницы
 * протокола (предзагрузка маршрута без данных), нажатие — прогревает данные.
 */
export function useProtocolIntent(): { preload: (id: string) => void; warm: (id: string) => void } {
  const router = useRouter();
  const queryClient = useQueryClient();
  return useMemo(
    () => ({
      preload: (id: string) => {
        router.preloadRoute({ to: '/protocols/$id', params: { id } }).catch(() => undefined);
      },
      warm: (id: string) => warmProtocol(queryClient, id),
    }),
    [router, queryClient],
  );
}
